import {
	DollarOutlined,
	DownCircleOutlined,
	ExclamationCircleOutlined,
	FileDoneOutlined,
	FileTextOutlined,
	ReloadOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Menu, Modal, Table, Tag, Tooltip } from 'antd';
import { midMonthPayApi } from 'Api/midMonthPays';
import DownloadComponent from 'components/Global/Download';
import Ellipse from 'components/Global/Ellipse';
import dayjs from 'dayjs';
import { useState } from 'react';
import { FaFileExcel } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Paid, PaymentUnderProcess, SentToBank } from 'utils/svgs';
import { formatDate, toastText } from 'utils/utils';
import AddModal from '../AddModal';
import LogsModal from '../LogsModal';
import styles from './index.module.scss';

const { Column } = Table;

type Props = {
	tableData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	isLoading: boolean;
	updateMidMonthStatus: (id: string, status: string) => void;
	fetchMidMonthData: any;
};

const MidMonthPayTable = (props: Props) => {
	const {
		tableData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		isLoading,
		updateMidMonthStatus,
		fetchMidMonthData,
	} = props;

	const [selectedPayrollCurrency, setSelectedPayrollCurrency] = useState<
		string | null
	>(null);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedMidMonthId, setSelectedMidMonthId] = useState<string | null>(
		null
	);
	const [isMidMonthLoading, setIsMidMonthLoading] = useState(false);
	const [midMonthDetailsData, setMidMonthDetailsData] = useState([]);
	const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);

	const [accountVerification, setAccountVerification] = useState<
		Record<string, boolean>
	>({});
		const [isBankErrorModalVisible, setIsBankErrorModalVisible] =
			useState(false);


	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	const navigate = useNavigate();
	

	const fetchBankDetailsForPayment = async (midmonthId: string) => {
		const findPayroll = tableData.find((m: any) => m.id === midmonthId);
		try {
			setIsMidMonthLoading(true);
			const bankDetails =
				await midMonthPayApi.getBankDetailsForMidMonthDetails({
					employeeId: findPayroll.employeeId,
				});

			const verified = bankDetails?.data?.data?.[0]?.isVerified ?? false;
			const details = bankDetails?.data?.data || [];

		if (details.length === 0 ) {
			setIsBankErrorModalVisible(true);
			return;
		}

			setAccountVerification((prev) => ({
				...prev,
				[midmonthId]: verified,
			}));

			// Trigger table re-render immediately
			setMidMonthDetailsData((prev: any) =>
				prev.map((item: any) =>
					item.id === midmonthId
						? { ...item, accountVerified: verified }
						: item
				)
			);
			setMidMonthDetailsData(bankDetails.data.data);
			setIsModalVisible(true);
		} catch (error: any) {
			toastText(
				error.response?.data?.message ??
					'Something went wrong in fetching mid-month details',
				'error'
			);
		} finally {
			setIsMidMonthLoading(false);
		}
	};

	const handleLogsOpen = (midMonthId: string) => {
		// Commented out API call for now
		// fetchPaymentLogs(midMonthId);

		// Just open the modal
		setSelectedMidMonthId(midMonthId);
		setIsLogsModalVisible(true);
	};

	const showModal = (midmonthId: string) => {
		fetchBankDetailsForPayment(midmonthId);
	};

	const handleModalOpen = (midmonthId: string) => {
		showModal(midmonthId);
		setSelectedMidMonthId(midmonthId);
	};

	const handleMenuClick = (record: any, e: any) => {
		if (record.status === e.key) {
			return;
		} else {
			updateMidMonthStatus(record.id, e.key);
		}
	};

		const handleRedirect = () => {
			handleErrorModal();
			    navigate(
					'/settings/configurations/company-setup?tab=bankDetails'
				);

		};

		const handleErrorModal =()=>{
	  setIsBankErrorModalVisible(false);
		}

	const statusMenu = (record: any) => (
		<Menu onClick={(e) => handleMenuClick(record, e)}>
			<Menu.Item key="PAID" className={styles.menuItem}>
				<span className={styles['menuIcon']}>
					<Paid />
				</span>
				Paid
			</Menu.Item>
		</Menu>
	);

	const handleRegenerate = async (midMonthPayId: string) => {
		try {
			const response = await midMonthPayApi.regenerateCsv({
				id: midMonthPayId,
			});
			toastText('Mid month csv has been re generated', 'success');
			fetchMidMonthData();
		} catch (error: any) {
			let message = 'Something went wrong in regenerating payslip';

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		}
	};
	return (
		<>
			<div>
				<Table
					dataSource={tableData}
					scroll={{ y: 'calc(100vh - 329px)' }}
					pagination={{
						total: totalRecords,
						current: currentPage,
						pageSize: pageSize,
						showSizeChanger: false,
						pageSizeOptions: [10, 20, 50, 100, 200],
					}}
					onChange={tableChangeHandler}
					rowKey={(record) => record.id}
					loading={isLoading}
				>
					<Column
						title="Created Date"
						dataIndex="createdAt"
						key="createdAt"
						className="bg-white"
						sorter={true}
						width={'12%'}
						render={(value) => {
							return formatDate(
								value,
								profileData?.companyDateFormat ?? 'DD/MM/YYYY'
							);
						}}
					/>
					<Column
						title="Details"
						dataIndex="createdAt"
						key="details"
						className="bg-white"
						render={(createdAt, record: any) => {
							const formattedCreatedTime =
								dayjs(createdAt).format('hh:mm A');
							let text = `This system-generated file was created on ${dayjs(
								createdAt
							).format(
								'DD/MM/YYYY'
							)} at ${formattedCreatedTime}.`;

							if (record?.updatedById && record?.updatedAt) {
								const formattedUpdatedTime = dayjs(
									record.updatedAt
								).format('hh:mm A');
								const formattedUpdatedDate = dayjs(
									record.updatedAt
								).format('DD/MM/YYYY');
								text += ` It was last updated on ${formattedUpdatedDate} at ${formattedUpdatedTime}.`;
							}

							return (
								<Ellipse
									maxLength={100}
									message={text}
									isTooltip={true}
									tooltipMessage={text}
									key={text}
								/>
							);
						}}
					/>
					<Column
						title="Re Generated By"
						dataIndex="updatedBy"
						key="updatedBy"
						width={'15%'}
						className="bg-white"
						render={(text) => text?.fullName}
					/>
					<Column
						title="Status"
						dataIndex="status"
						key="status"
						width={'20%'}
						className={`${styles['statusColumn']} bg-white`}
						render={(value, record) => {
							let statusText = '';
							let statusClass = '';
							let StatusIcon = null;

							if (value === 'SENT_TO_BANK') {
								statusText = 'Sent to Bank';
								statusClass = styles['status-sent-to-bank'];
								StatusIcon = <SentToBank />;
							} else if (value === 'PAYMENT_UNDER_PROCESS') {
								statusText = 'Payment under Process';
								statusClass =
									styles['status-payment-under-process'];
								StatusIcon = <PaymentUnderProcess />;
							} else if (value === 'FILE_GENERATED') {
								statusText = 'File Generated';
								statusClass = styles['status-file-generated'];
								StatusIcon = <FileDoneOutlined />;
							} else if (value === 'PAID') {
								statusText = 'Paid';
								statusClass = styles['status-paid'];
								StatusIcon = <Paid />;
							} else if (value === 'PROCESSING') {
								statusText = 'Processing';
								statusClass = styles['status-processing'];
								StatusIcon = <SyncOutlined />;
							} else {
								statusText = 'Please select status';
								statusClass = styles['status-placeholder'];
								StatusIcon = <ExclamationCircleOutlined />;
							}

							return (
								<div className={styles['statusContainer']}>
									<Dropdown
										overlay={statusMenu(record)}
										trigger={['click']}
									>
										<Button
											type="link"
											onClick={(e) => e.preventDefault()}
											className={
												styles['dropdownTrigger']
											}
										>
											<DownCircleOutlined />
										</Button>
									</Dropdown>

									<Tag
										bordered={false}
										className={`${styles['statusTag']} ${statusClass}`}
									>
										<div className={styles['statusIcon']}>
											{StatusIcon}
											<span>{statusText}</span>
										</div>
									</Tag>
								</div>
							);
						}}
					/>
					<Column
						title="File"
						dataIndex="documentUrl"
						className="bg-white"
						key="documentUrl"
						width={'10%'}
						render={(text, record: any) => (
							<div>
								<DownloadComponent
									documentUrl={record.attachmentUrl}
									icon={
										<FaFileExcel
											color="#217346"
											size={20}
										/>
									}
									showTooltip={true}
									isLink={false}
									downloadText={
										record.documentName.split('.')[0]
									}
									tooltipText={
										record.documentName.split('.')[0]
									}
									maxLength={20}
								/>
								{record.status !== 'PAID' && (
									<Tooltip title="Regenerate the csv file">
										<Button
											type="text"
											icon={<ReloadOutlined />}
											onClick={() =>
												handleRegenerate(record.id)
											}
											style={{ color: '#584495' }}
										/>
									</Tooltip>
								)}
							</div>
						)}
					/>
					{/* <Column
						title="Initiate Payment"
						dataIndex="initiatePayment"
						key="initiatePayment"
						width={170}
						align="center"
						sorter={false}
						className="bg-white"
						render={(_, record: any) => {
							const isDisabled =
								record.status !== 'FILE_GENERATED';

							return (
								<Tooltip
									title={
										isDisabled
											? 'Payment initiation is available once the mid-month pay file is generated and bank details are verified.'
											: 'Click to initiate payment'
									}
								>
									{!isDisabled ? (
										<div
											onClick={() => {
												setSelectedPayrollCurrency(
													record.currency
												);
												handleModalOpen(record.id);
											}}
											style={{
												cursor: 'pointer',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '19px',
												width: '100%',
												height: '100%',
											}}
										>
											<DollarOutlined
											// style={{
											// 	color: '#584495',
											// }}
											/>
										</div>
									) : (
										<div className="text-gray-400">N/A</div>
									)}
								</Tooltip>
							);
						}}
					/>
					<Column
						title="Logs"
						dataIndex="logs"
						key="logs"
						width={100}
						align="center"
						className="bg-white"
						render={(_, record: any) => (
							<Tooltip
								title={
									record.isLogExists
										? 'View Payment Logs'
										: 'No Payment Logs'
								}
							>
								<Button
									type="text"
									disabled={!record.isLogExists}
									icon={<FileTextOutlined />}
									style={{
										fontSize: '21px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
									onClick={() => handleLogsOpen(record.id)}
								/>
							</Tooltip>
						)}
					/> */}
				</Table>
				<AddModal
					isOpen={isModalVisible}
					handleCancel={() => {
						setIsModalVisible(false);
						setSelectedMidMonthId(null);
					}}
					isBankLoading={isLoading}
					bankDetailsData={midMonthDetailsData}
					payRollId={selectedMidMonthId || ''}
					handleBankDetails={setMidMonthDetailsData}
					currency={selectedPayrollCurrency || ''}
					fetchMidMonthData={fetchMidMonthData}
				/>
				{/* Payment Logs Modal */}
				{isLogsModalVisible && selectedMidMonthId && (
					<LogsModal
						isOpen={isLogsModalVisible}
						handleCancel={() => setIsLogsModalVisible(false)}
						midMonthId={selectedMidMonthId}
						currency={selectedPayrollCurrency || ''}
						updateMidMonthStatus={(
							midMonthId: string,
							status: string
						) => {
							updateMidMonthStatus(midMonthId, status);
						}}
					/>
				)}
			</div>

			<Modal
				open={isBankErrorModalVisible}
				onCancel={() => setIsBankErrorModalVisible(false)}
				footer={null}
				centered
				width={520}
				closable={false}
			>
				<div style={{ textAlign: 'center', padding: '10px 5px' }}>
					<div
						style={{
							width: 70,
							height: 70,
							borderRadius: '50%',
							background: '#FFF7E6',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							margin: '0 auto',
						}}
					>
						<ExclamationCircleOutlined
							style={{ fontSize: 38, color: '#faad14' }}
						/>
					</div>

					<h2 style={{ marginTop: 16, fontWeight: 700 }}>
						Bank Details Required
					</h2>

					<p
						style={{
							fontSize: 15,
							color: '#595959',
							marginTop: 8,
							lineHeight: 1.6,
						}}
					>
						To process payments, please add and verify a bank
						account in the Company Setup section.
					</p>

					<div
						style={{
							textAlign: 'left',
							background: '#fafafa',
							padding: '16px 20px',
							borderRadius: 8,
							marginTop: 24,
							border: '1px solid #f0f0f0',
						}}
					>
						<p
							style={{
								fontWeight: 600,
								marginBottom: 10,
								color: '#444',
							}}
						>
							Bank must meet the following requirements:
						</p>

						<ul
							style={{
								paddingLeft: 20,
								lineHeight: 1.6,
								margin: 0,
							}}
						>
							<li>
								Bank Type: <b>First Capital Bank</b>
							</li>
							<li>
								Account status: <b>Verified</b>
							</li>
							<li>
								Currency:{' '}
								<span
									style={{
										background: '#e6f7ff',
										padding: '2px 6px',
										borderRadius: 4,
										fontWeight: 600,
									}}
								>
									ZMW
								</span>
							</li>
							<li>Fund Transfer must be enabled</li>
						</ul>
					</div>

					<Button
						type="primary"
						size="large"
						style={{ marginTop: 26, borderRadius: 6 }}
						onClick={handleRedirect}
					>
						Go to Company Setup
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default MidMonthPayTable;
